// frontend/packages/business-console/src/components/content/ArrayObjectField.jsx
//
// Renders an array of objects as collapsible cards. Used for the sections[]
// array in page frontmatter and any other array-of-objects schema field.
//
// Handles two spec formats from the backend introspection:
//   1. Direct object items: { type: "array", items: { type: "object", fields: {...} } }
//   2. Union items (discriminated): { type: "array", items: { type: "union", options: [...] } }

import React, { useState, useCallback } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";

import { setAtPath } from "../../utils/contentEditorHelpers";

const SECTION_TYPE_LABELS = {
  featureGrid: "Feature Grid",
  proof: "Proof Strip",
  stats: "Stats",
  testimonials: "Testimonials",
  featuredLocations: "Featured Locations",
  process: "Process Steps",
  standards: "Standards",
  servicesPreview: "Services Preview",
  teamPreview: "Team Preview",
  gallery: "Gallery",
  neighborhoodIndex: "Neighborhood Index",
  faq: "FAQ",
  cta: "Call to Action",
  richText: "Rich Text",
};

function humanizeSectionType(type) {
  if (!type) return "Section";
  return (
    SECTION_TYPE_LABELS[type] ||
    String(type)
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/[-_]/g, " ")
      .replace(/^./, (c) => c.toUpperCase())
  );
}

const TYPE_COLORS = {
  featureGrid: "#3498db",
  proof: "#9b59b6",
  stats: "#2ecc71",
  testimonials: "#f39c12",
  process: "#1abc9c",
  cta: "#e74c3c",
  gallery: "#e67e22",
  faq: "#34495e",
  richText: "#7f8c8d",
  servicesPreview: "#2980b9",
  teamPreview: "#8e44ad",
  standards: "#16a085",
  featuredLocations: "#d35400",
  neighborhoodIndex: "#c7a24b",
};

// ---------------------------------------------------------------------------
// Spec resolution — handles both "object" and "union" item formats
// ---------------------------------------------------------------------------

function getTypeFieldValue(typeFieldSpec) {
  if (!typeFieldSpec) return null;
  if (typeFieldSpec.type === "enum" && typeFieldSpec.values?.length > 0) return typeFieldSpec.values[0];
  if (typeFieldSpec.type === "literal" && typeFieldSpec.value) return typeFieldSpec.value;
  if (typeFieldSpec.default) return String(typeFieldSpec.default);
  return null;
}

function getItemSpec(spec, item) {
  // Format 1: discriminator/variants (pre-processed)
  if (spec?.items?.discriminator && spec?.items?.variants && item?.type) {
    const variant = spec.items.variants[item.type];
    if (variant?.fields) return variant;
  }

  // Format 2: union options — match by the "type" field in each option
  if (spec?.items?.type === "union" && Array.isArray(spec?.items?.options)) {
    for (const opt of spec.items.options) {
      if (opt?.type === "object" && opt?.fields?.type && item?.type) {
        const typeVal = getTypeFieldValue(opt.fields.type);
        if (typeVal === item.type) return opt;
      }
    }
    // Fallback: first object option
    const first = spec.items.options.find((o) => o?.type === "object");
    if (first) return first;
  }

  // Format 3: plain object items
  if (spec?.items?.type === "object" && spec?.items?.fields) return spec.items;
  return spec?.items || {};
}

function getAvailableTypes(spec) {
  // discriminator/variants format
  if (spec?.items?.discriminator && spec?.items?.variants) {
    return Object.keys(spec.items.variants);
  }

  // union options format
  if (spec?.items?.type === "union" && Array.isArray(spec?.items?.options)) {
    const types = [];
    for (const opt of spec.items.options) {
      if (opt?.type === "object" && opt?.fields?.type) {
        const val = getTypeFieldValue(opt.fields.type);
        if (val) types.push(val);
      }
    }
    return types;
  }

  return [];
}

function getItemTitle(item, index) {
  if (item?.type) return humanizeSectionType(item.type);
  if (item?.title) return item.title;
  if (item?.name) return item.name;
  if (item?.label) return item.label;
  if (item?.headline) return item.headline;
  return `Item ${index + 1}`;
}

function getValueAtPath(obj, path) {
  if (!obj || !path) return undefined;
  const segments = String(path).split(".").filter(Boolean);
  let cur = obj;
  for (const seg of segments) {
    if (cur == null || typeof cur !== "object") return undefined;
    cur = cur[seg];
  }
  return cur;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function ArrayObjectField({
  label,
  path,
  spec,
  frontmatterObj,
  showAllOptional,
  onChange,
  onPickMedia,
}) {
  const arr = Array.isArray(getValueAtPath(frontmatterObj, path))
    ? getValueAtPath(frontmatterObj, path)
    : [];

  const [expandedIndex, setExpandedIndex] = useState(null);
  const [addMenuAnchor, setAddMenuAnchor] = useState(null);

  const availableTypes = getAvailableTypes(spec);
  const hasTypeSelector = availableTypes.length > 0;

  const updateArray = useCallback(
    (nextArr) => {
      onChange(setAtPath(frontmatterObj, path, nextArr));
    },
    [frontmatterObj, onChange, path],
  );

  const handleMoveUp = (index) => {
    if (index <= 0) return;
    const next = [...arr];
    [next[index - 1], next[index]] = [next[index], next[index - 1]];
    updateArray(next);
  };

  const handleMoveDown = (index) => {
    if (index >= arr.length - 1) return;
    const next = [...arr];
    [next[index], next[index + 1]] = [next[index + 1], next[index]];
    updateArray(next);
  };

  const handleDelete = (index) => {
    const next = arr.filter((_, i) => i !== index);
    updateArray(next);
    if (expandedIndex === index) setExpandedIndex(null);
  };

  const handleAddItem = (type) => {
    setAddMenuAnchor(null);
    const newItem = type ? { type } : {};

    // Seed defaults from the matching option spec
    if (type) {
      const itemSpec = getItemSpec(spec, { type });
      if (itemSpec?.fields) {
        for (const [key, fieldSpec] of Object.entries(itemSpec.fields)) {
          if (key === "type") continue;
          if (fieldSpec.default !== undefined) {
            newItem[key] = fieldSpec.default;
          }
        }
      }
    }

    const next = [...arr, newItem];
    updateArray(next);
    setExpandedIndex(next.length - 1);
  };

  // Lazy import to avoid circular dependency
  const FrontmatterField = require("./FrontmatterField").default;

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
        {label}
      </Typography>

      {arr.length === 0 && (
        <Typography variant="body2" sx={{ opacity: 0.5, mb: 1 }}>
          No items yet.
        </Typography>
      )}

      {arr.map((item, index) => {
        const itemTitle = getItemTitle(item, index);
        const itemSpec = getItemSpec(spec, item);
        const fields = itemSpec?.fields || {};
        const typeColor = TYPE_COLORS[item?.type] || "#7f8c8d";

        return (
          <Accordion
            key={`${path}-${index}`}
            expanded={expandedIndex === index}
            onChange={(_, isExpanded) => setExpandedIndex(isExpanded ? index : null)}
            variant="outlined"
            sx={{ mb: 1, "&:before": { display: "none" } }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{ minHeight: 48, "& .MuiAccordionSummary-content": { alignItems: "center", gap: 1 } }}
            >
              {item?.type && (
                <Chip
                  label={humanizeSectionType(item.type)}
                  size="small"
                  sx={{
                    fontWeight: 700,
                    fontSize: 11,
                    borderColor: typeColor,
                    color: typeColor,
                    background: "transparent",
                  }}
                  variant="outlined"
                />
              )}
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {itemTitle !== humanizeSectionType(item?.type) ? itemTitle : ""}
              </Typography>

              <Box sx={{ ml: "auto", display: "flex", gap: 0.25 }} onClick={(e) => e.stopPropagation()}>
                <IconButton
                  size="small"
                  disabled={index === 0}
                  onClick={() => handleMoveUp(index)}
                  aria-label="Move up"
                >
                  <ArrowUpwardIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  disabled={index === arr.length - 1}
                  onClick={() => handleMoveDown(index)}
                  aria-label="Move down"
                >
                  <ArrowDownwardIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => handleDelete(index)}
                  aria-label="Delete item"
                  color="error"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            </AccordionSummary>

            <AccordionDetails>
              <Stack spacing={1.25}>
                {Object.entries(fields).map(([childKey, childSpec]) => {
                  if (childKey === "type") return null;
                  return (
                    <FrontmatterField
                      key={`${path}.${index}.${childKey}`}
                      path={`${path}.${index}.${childKey}`}
                      spec={childSpec}
                      frontmatterObj={frontmatterObj}
                      showAllOptional={true}
                      onChange={onChange}
                      onPickMedia={onPickMedia}
                    />
                  );
                })}
              </Stack>
            </AccordionDetails>
          </Accordion>
        );
      })}

      {hasTypeSelector ? (
        <>
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={(e) => setAddMenuAnchor(e.currentTarget)}
            sx={{ mt: 1 }}
          >
            Add Section
          </Button>
          <Menu
            anchorEl={addMenuAnchor}
            open={Boolean(addMenuAnchor)}
            onClose={() => setAddMenuAnchor(null)}
          >
            {availableTypes.map((vt) => (
              <MenuItem key={vt} onClick={() => handleAddItem(vt)}>
                {humanizeSectionType(vt)}
              </MenuItem>
            ))}
          </Menu>
        </>
      ) : (
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => handleAddItem(null)}
          sx={{ mt: 1 }}
        >
          Add Item
        </Button>
      )}
    </Box>
  );
}
